import { useId, useRef } from 'react'
import { cn } from '@/lib/utils'
import { createRandom } from '@/lib/random'
import { useInView } from '@/hooks/use-in-view'
import { smoothPath, svgId, toPoints } from '@/lib/chart'

const WIDTH = 560
const HEIGHT = 260
const HISTORY_POINTS = 26
const FORECAST_POINTS = 24

const buildData = () => {
    const random = createRandom(31)
    const history: Array<number> = []
    let value = 4200

    for (let index = 0; index < HISTORY_POINTS; index += 1) {
        const payday = index % 13 === 2 ? 2600 : 0
        const rent = index % 13 === 4 ? -1850 : 0

        value += payday + rent - 95 + (random() - 0.5) * 220
        history.push(value)
    }

    const median: Array<number> = [value]
    const upper: Array<number> = [value]
    const lower: Array<number> = [value]

    for (let index = 1; index < FORECAST_POINTS; index += 1) {
        const absolute = HISTORY_POINTS - 1 + index
        const payday = absolute % 13 === 2 ? 2600 : 0
        const rent = absolute % 13 === 4 ? -1850 : 0

        value += payday + rent - 95
        median.push(value)

        const spread = 90 + index * 38

        upper.push(value + spread)
        lower.push(value - spread)
    }

    return { history, median, upper, lower }
}

const DATA = buildData()
const ALL = [...DATA.history, ...DATA.upper, ...DATA.lower]
const SCALE = {
    width: WIDTH,
    height: HEIGHT,
    padTop: 20,
    padBottom: 20,
    min: Math.min(...ALL) - 200,
    max: Math.max(...ALL) + 200,
    total: HISTORY_POINTS + FORECAST_POINTS - 1
}

const historyPoints = toPoints(DATA.history, SCALE)
const medianPoints = toPoints(DATA.median, { ...SCALE, offset: HISTORY_POINTS - 1 })
const upperPoints = toPoints(DATA.upper, { ...SCALE, offset: HISTORY_POINTS - 1 })
const lowerPoints = toPoints(DATA.lower, { ...SCALE, offset: HISTORY_POINTS - 1 })
const todayX = historyPoints[historyPoints.length - 1].x

const bandPath = (() => {
    const top = smoothPath(upperPoints)
    const bottom = smoothPath([...lowerPoints].reverse()).replace(/^M/, 'L')

    return `${top} ${bottom} Z`
})()

/** Balance forecast with a widening band of likely outcomes. */
export const PredictionChart = ({ className }: { className?: string }) => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { threshold: 0.4 })
    const bandId = `band-${svgId(useId())}`

    return (
        <div ref={ref} className={cn('glass-dark grain relative overflow-hidden rounded-[30px] p-5 sm:p-7', className)}>
            <div className='flex flex-wrap items-center justify-between gap-3'>
                <div>
                    <p className='text-[11px] font-medium uppercase tracking-[0.24em] text-cream/50'>
                        Checking account
                    </p>

                    <p className='mt-1 text-sm text-cream/70'>
                        The past 8 weeks, then the coming 7
                    </p>
                </div>

                <div className='flex items-center gap-4 text-[11px] text-cream/55'>
                    <span className='flex items-center gap-1.5'>
                        <span className='h-0.5 w-4 rounded-full bg-citron' />
                        So far
                    </span>

                    <span className='flex items-center gap-1.5'>
                        <span className='h-2 w-4 rounded-sm bg-citron/25' />
                        Expected range
                    </span>
                </div>
            </div>

            <div className='relative mt-6 aspect-[560/260] w-full'>
                <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className='absolute inset-0 h-full w-full overflow-visible' aria-hidden='true'>
                    <defs>
                        <linearGradient id={bandId} x1='0' y1='0' x2='1' y2='0'>
                            <stop offset='0%' stopColor='#e2f37a' stopOpacity='0.32' />
                            <stop offset='100%' stopColor='#8fae62' stopOpacity='0.12' />
                        </linearGradient>
                    </defs>

                    {[0.2, 0.4, 0.6, 0.8].map(line => (
                        <line key={line} x1='0' x2={WIDTH} y1={HEIGHT * line} y2={HEIGHT * line} stroke='rgba(255,255,255,0.06)' />
                    ))}

                    <line x1={todayX} x2={todayX} y1='0' y2={HEIGHT} stroke='rgba(243,239,230,0.25)' strokeDasharray='3 5' />

                    <path
                        d={bandPath}
                        fill={`url(#${bandId})`}
                        className='transition-opacity duration-1000'
                        style={{ opacity: inView ? 1 : 0, transitionDelay: inView ? '1600ms' : '0ms' }}
                    />

                    <path
                        d={smoothPath(historyPoints)}
                        fill='none'
                        stroke='#e2f37a'
                        strokeWidth='2.4'
                        strokeLinecap='round'
                        pathLength={1}
                        strokeDasharray='1'
                        style={{ strokeDashoffset: inView ? 0 : 1, transition: 'stroke-dashoffset 1.8s cubic-bezier(0.22, 1, 0.36, 1) 200ms' }}
                    />

                    <path
                        d={smoothPath(medianPoints)}
                        fill='none'
                        stroke='#e2f37a'
                        strokeOpacity='0.8'
                        strokeWidth='2'
                        strokeDasharray='5 6'
                        className='transition-opacity duration-700'
                        style={{ opacity: inView ? 1 : 0, transitionDelay: inView ? '1900ms' : '0ms' }}
                    />
                </svg>

                <span
                    className='absolute top-1 -translate-x-1/2 rounded-full bg-cream px-2.5 py-1 text-[10px] font-medium text-black'
                    style={{ left: `${(todayX / WIDTH) * 100}%` }}
                >
                    Today
                </span>
            </div>

            <div className='mt-5 grid gap-3 sm:grid-cols-2'>
                <p className='rounded-2xl bg-white/[0.04] px-4 py-3 text-xs leading-relaxed text-cream/70'>
                    Rent leaves on the 1st and pay arrives on the 15th and 30th. Verdane has it noted.
                </p>

                <p className='rounded-2xl bg-citron/10 px-4 py-3 text-xs leading-relaxed text-citron'>
                    A 90% chance your balance holds above $1,000 for the next seven weeks.
                </p>
            </div>
        </div>
    )
}
